import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mountain, CheckCircle2, ArrowRight, MessageCircle, Camera, X, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '919871155963'; // central GoHimalayan WA

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

const PROPERTY_TYPES = [
  { v: 'homestay', l: 'Homestay' },
  { v: 'cottage', l: 'Cottage' },
  { v: 'lodge', l: 'Lodge' },
  { v: 'villa', l: 'Villa' },
  { v: 'resort', l: 'Resort' },
  { v: 'guesthouse', l: 'Guesthouse' },
];

// Tick-list options. Kept short on purpose — hosts skip long lists. Each chip
// is one tap. Curators can still add nuance through the optional description
// field below. Keep these synced with the icon mapping in CollectionLanding
// and the curator UI when we add filters there.
const AMENITY_OPTIONS = [
  'Fireplace / wood stove', 'Mountain view', 'River view', 'River access',
  'Garden / lawn', 'Sit-out / veranda', 'Full kitchen access', 'BBQ / grill',
  'Library / book corner', 'Yoga deck', 'Pet-friendly', 'Wheelchair-accessible',
  'Kids play area', 'Parking on-site', 'Wi-Fi', 'Heating',
  'Hot water 24×7', 'Electric back-up', 'In-house chef / meals included',
];

const EXPERIENCE_OPTIONS = [
  'Trout fishing', 'River swimming / dip', 'Forest walks', 'Birdwatching',
  'Bonfire arranged', 'Star-gazing', 'Cooking with host', 'Yoga / meditation',
  'Trekking from doorstep', 'Mountain biking', 'Local village walk',
  'Orchard / farm visit', 'Sunrise / sunset point', 'Picnic spot nearby',
  'Local market / mela', 'Cultural / craft session', 'Heli-skiing nearby',
  'Camping option',
];

export default function Apply() {
  const navigate = useNavigate();
  const [valleys, setValleys] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [uploadedPhotos, setUploadedPhotos] = useState([]); // [{url, thumbnail, filename}]
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    display_name: '',
    host_name: '',
    whatsapp_number: '',
    email: '',
    instagram: '',
    valley_slug: '',
    property_type: 'homestay',
    altitude_m: '',
    tagline: '',
    description: '',
    image_urls_text: '',
    best_for: '',
    experience_types: '',
    booking_source_url: '',
    // Inventory & capacity
    inv_cottages: '',
    inv_rooms: '',
    inv_family_rooms: '',
    inv_suites: '',
    inv_tents: '',
    sleeps_max: '',
    // Pricing
    starting_price_inr: '',
    price_unit: 'per_room',       // per_room | per_person
    includes_breakfast: true,
    // Family Estate Collection eligibility
    whole_cottage_only: false,
    // Tick-list amenities + experiences (replaces the free-text "tell us
    // about the fireplace, river…" which hosts kept skipping).
    amenities: [],
    experiences: [],
    // Consent
    consent_discount: false,
  });

  // Toggle a value in an array-state field. Used by the amenities/experiences
  // chip selectors below.
  const toggleArr = (key, value) => {
    setForm(prev => {
      const arr = prev[key] || [];
      const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
      return { ...prev, [key]: next };
    });
  };

  useEffect(() => {
    fetch(`${API}/api/apply/valleys`)
      .then(r => r.json())
      .then(d => setValleys(d.valleys || []));
  }, []);

  // Group valleys by country → state for the dropdown
  const COUNTRY_LABELS = { IN: 'India', NP: 'Nepal', BT: 'Bhutan' };
  const COUNTRY_ORDER = ['IN', 'NP', 'BT'];
  const byCountry = valleys.reduce((acc, v) => {
    const c = v.country || 'IN';
    if (!acc[c]) acc[c] = {};
    const state = (v.state_slug || 'other').replace(/^bhutan-|^nepal-/, '').replace(/-/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());
    if (!acc[c][state]) acc[c][state] = [];
    acc[c][state].push(v);
    return acc;
  }, {});

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).slice(0, 25 - uploadedPhotos.length);
    if (files.length === 0) return;

    setUploading(true);
    setError('');
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));

    try {
      const r = await fetch(`${API}/api/apply/upload-photos`, {
        method: 'POST',
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Upload failed');

      // Build relative URLs (same-origin) and create local thumbnails.
      // Storing absolute preview-domain URLs in the DB has bitten us before — when the
      // domain changes (preview rotation, prod rename), the old URLs become unreachable.
      const newPhotos = data.uploaded.map((p, i) => ({
        url: p.url,  // keep relative — same-origin in any environment
        filename: p.filename,
        thumbnail: URL.createObjectURL(files[i]),
      }));
      setUploadedPhotos(prev => [...prev, ...newPhotos]);

      if (data.errors && data.errors.length > 0) {
        setError(`${data.errors.length} file(s) skipped: ${data.errors.map(e => e.error).join(', ')}`);
      }
    } catch (err) {
      setError(`Photo upload failed: ${err.message}. You can still submit the form and send photos via WhatsApp.`);
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (idx) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    // Combine: uploaded photo URLs + any pasted URLs
    const pastedUrls = form.image_urls_text
      .split(/[\n,]/)
      .map(s => s.trim())
      .filter(s => s.startsWith('http'));
    const image_urls = [...uploadedPhotos.map(p => p.url), ...pastedUrls];

    const payload = {
      ...form,
      image_urls,
      altitude_m: form.altitude_m ? parseInt(form.altitude_m, 10) : null,
      inv_cottages: parseInt(form.inv_cottages, 10) || 0,
      inv_rooms: parseInt(form.inv_rooms, 10) || 0,
      inv_family_rooms: parseInt(form.inv_family_rooms, 10) || 0,
      inv_suites: parseInt(form.inv_suites, 10) || 0,
      inv_tents: parseInt(form.inv_tents, 10) || 0,
      sleeps_max: form.sleeps_max ? parseInt(form.sleeps_max, 10) : null,
      starting_price_inr: form.starting_price_inr ? parseInt(form.starting_price_inr, 10) : null,
      price_unit: form.price_unit || 'per_room',
      includes_breakfast: !!form.includes_breakfast,
    };
    delete payload.image_urls_text;
    delete payload.consent_discount;

    try {
      const r = await fetch(`${API}/api/apply/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Submission failed');
      // Redirect to the host-side confirmation page (shows what was captured + recovery CTAs)
      if (data.gohimalayan_id) {
        navigate(`/apply/thanks/${encodeURIComponent(data.gohimalayan_id)}`);
        return;
      }
      setSuccess({ ...data, photo_count: image_urls.length });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-12">
        <div className="max-w-lg bg-white rounded-lg shadow-md border border-stone-200 p-10 text-center" data-testid="apply-success">
          <CheckCircle2 className="mx-auto mb-4 text-emerald-600" size={48} strokeWidth={1.4} />
          <h1 className="text-3xl text-stone-900 mb-3" style={pf}>Thank you.</h1>
          <p className="text-sm text-stone-600 leading-relaxed mb-4">
            Your application has been received. Our editorial team reviews every submission within 48 hours.
          </p>
          <p className="text-[11px] tracking-wider uppercase text-stone-400 mb-1">Reference</p>
          <p className="font-mono text-sm text-stone-800 mb-6">{success.gohimalayan_id}</p>
          <p className="text-xs text-stone-500 mb-6">{success.next_step}</p>
          {success.photo_count === 0 && (
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi GoHimalayan — I just submitted my application (${success.gohimalayan_id}). Sending photos now.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block mb-3 px-5 py-2.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700"
              data-testid="success-send-photos-wa"
            >
              <MessageCircle size={14} className="inline mr-2" /> Send photos via WhatsApp now
            </a>
          )}
          <a href="/discovered" className="inline-block px-5 py-2 text-sm bg-stone-900 text-white rounded hover:bg-stone-800">
            Browse curated stays →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <div className="bg-stone-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="flex items-center gap-2 mb-6">
            <Mountain size={20} className="text-amber-300" strokeWidth={1.5} />
            <span className="text-[10px] tracking-[0.4em] uppercase text-amber-300 font-semibold">For independent Himalayan hosts</span>
          </div>
          <h1 className="text-4xl sm:text-5xl mb-4 leading-tight" style={pf}>
            Experiential Himalayan stays.<br />
            <em className="text-amber-300">Hand-picked.</em>
          </h1>
          <p className="text-base text-stone-300 leading-relaxed max-w-xl">
            Cottages with stories. Lodges with souls. Homes with hosts who'll remember a traveller's name.
            We feature properties with editorial stories, cinematic photo cards, and direct WhatsApp bookings —
            <strong className="text-amber-200"> free, forever, no commission.</strong>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8 text-xs text-stone-300">
            <div className="border-t-2 border-amber-300 pt-3">
              <p className="font-semibold text-white mb-1">Distinctive</p>
              <p>Cottages, manors, lodges, retreats — built and run with care.</p>
            </div>
            <div className="border-t-2 border-amber-300 pt-3">
              <p className="font-semibold text-white mb-1">Hand-picked</p>
              <p>No resorts, no chains. Every stay earns its place editorially.</p>
            </div>
            <div className="border-t-2 border-amber-300 pt-3">
              <p className="font-semibold text-white mb-1">Free, forever</p>
              <p>Zero commission. Zero listing fee. WhatsApp bookings to you, direct.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={submit} className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="apply-form">
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded p-3" data-testid="apply-error">
            {error}
          </div>
        )}

        <Section title="The basics">
          <Field label="Property name *" testid="display_name">
            <input required value={form.display_name} onChange={e => update('display_name', e.target.value)} placeholder="e.g. Banpravas, Mukteshwar" className={inputC} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Host name" testid="host_name">
              <input value={form.host_name} onChange={e => update('host_name', e.target.value)} placeholder="Your name" className={inputC} />
            </Field>
            <Field label="WhatsApp number *" testid="whatsapp_number">
              <input required value={form.whatsapp_number} onChange={e => update('whatsapp_number', e.target.value)} placeholder="+91 / +977 / +975 …" className={inputC} />
            </Field>
          </div>
          <Field label="Email (optional)" testid="email">
            <input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@example.com" className={inputC} />
          </Field>
          <Field label="Instagram (optional)" testid="instagram">
            <input value={form.instagram} onChange={e => update('instagram', e.target.value)} placeholder="@yourhandle  ·  helps our team verify and follow up faster" className={inputC} />
          </Field>
        </Section>

        <Section title="Where & what">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Valley *" testid="valley_slug">
              <select required value={form.valley_slug} onChange={e => update('valley_slug', e.target.value)} className={inputC}>
                <option value="">— pick a valley —</option>
                {COUNTRY_ORDER.filter(c => byCountry[c]).map(c => (
                  <optgroup key={c} label={`—  ${COUNTRY_LABELS[c].toUpperCase()}  —`}>
                    {Object.entries(byCountry[c]).map(([state, vs]) => (
                      vs.map(v => (
                        <option key={v.slug} value={v.slug}>
                          {v.name}{Object.keys(byCountry[c]).length > 1 ? ` — ${state}` : ''}
                        </option>
                      ))
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>
            <Field label="Property type" testid="property_type">
              <select value={form.property_type} onChange={e => update('property_type', e.target.value)} className={inputC}>
                {PROPERTY_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Altitude (metres, optional)" testid="altitude_m">
            <input type="number" value={form.altitude_m} onChange={e => update('altitude_m', e.target.value)} placeholder="e.g. 2050" className={inputC} />
          </Field>
        </Section>

        <Section title="Rooms, guests & price">
          <p className="text-[11px] text-stone-500 -mt-1 leading-relaxed">
            Tell us how big your place is and what one night costs. Don't worry — guests see only your <em>starting price</em>, and you stay in control of final quotes via WhatsApp.
          </p>

          {/* Total guest capacity — the most important number for discovery filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Total guests you can host *" testid="sleeps_max">
              <input
                type="number"
                min="1"
                max="200"
                required
                value={form.sleeps_max}
                onChange={e => update('sleeps_max', e.target.value)}
                placeholder="e.g. 8"
                className={inputC}
              />
              <span className="block text-[10px] text-stone-400 mt-1">Across all rooms, at full occupancy.</span>
            </Field>
            <Field label="Starting price per night (INR) *" testid="starting_price_inr">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">₹</span>
                <input
                  type="number"
                  min="500"
                  max="200000"
                  required
                  value={form.starting_price_inr}
                  onChange={e => update('starting_price_inr', e.target.value)}
                  placeholder="e.g. 6000"
                  className={inputC + ' pl-6'}
                />
              </div>
              <span className="block text-[10px] text-stone-400 mt-1">The lowest nightly rate a guest could see.</span>
            </Field>
          </div>

          {/* Price unit + breakfast — small, inline */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="That price is" testid="price_unit">
              <select value={form.price_unit} onChange={e => update('price_unit', e.target.value)} className={inputC}>
                <option value="per_room">Per room / per night</option>
                <option value="per_person">Per person / per night</option>
                <option value="whole_property">For the whole property / per night</option>
              </select>
            </Field>
            <label className="flex items-center gap-2.5 mt-6 cursor-pointer" data-testid="apply-field-includes_breakfast">
              <input
                type="checkbox"
                checked={form.includes_breakfast}
                onChange={e => update('includes_breakfast', e.target.checked)}
                className="w-4 h-4 rounded border-stone-400 text-amber-600 focus:ring-amber-500 cursor-pointer"
              />
              <span className="text-sm text-stone-800">Includes <b>breakfast</b></span>
            </label>
          </div>

          {/* Inventory breakdown — granular but optional */}
          <div className="pt-2">
            <p className="block text-xs text-stone-700 mb-2">How many of each? <span className="text-stone-400">(fill what applies — leave the rest blank)</span></p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <InvField label="Rooms"        value={form.inv_rooms}        onChange={v => update('inv_rooms', v)}        testid="inv_rooms" />
              <InvField label="Cottages"     value={form.inv_cottages}     onChange={v => update('inv_cottages', v)}     testid="inv_cottages" />
              <InvField label="Family rooms" value={form.inv_family_rooms} onChange={v => update('inv_family_rooms', v)} testid="inv_family_rooms" />
              <InvField label="Suites"       value={form.inv_suites}       onChange={v => update('inv_suites', v)}       testid="inv_suites" />
              <InvField label="Tents"        value={form.inv_tents}        onChange={v => update('inv_tents', v)}        testid="inv_tents" />
            </div>
          </div>

          {/* Whole-cottage rental — eligibility for Family Estate Collection */}
          <label className="flex items-start gap-3 cursor-pointer p-3 bg-amber-50/60 border border-amber-200/70 rounded-md mt-2"
                 data-testid="apply-field-whole_cottage_only">
            <input
              type="checkbox"
              checked={form.whole_cottage_only}
              onChange={e => update('whole_cottage_only', e.target.checked)}
              className="w-4 h-4 mt-0.5 rounded border-stone-400 text-amber-600 focus:ring-amber-500 cursor-pointer"
            />
            <span className="text-sm text-stone-800 leading-snug">
              <b>Select if you also let out the entire place.</b>
              <br/><span className="text-xs text-stone-500">Optional — tick this if guests can book the whole property to themselves (no shared spaces, no other guests during their stay). Makes you eligible for our family-cottage collection.</span>
            </span>
          </label>
        </Section>

        {/* What's at the property + what guests can do — tick-lists replace
            the old free-text prompt that hosts kept leaving blank. */}
        <Section title="What's at the property">
          <p className="text-xs text-stone-500 -mt-1 mb-2">Tap everything that applies. Curators use these to write your story and make your listing searchable.</p>
          <ChipGroup
            label="On-property amenities"
            options={AMENITY_OPTIONS}
            value={form.amenities}
            onToggle={v => toggleArr('amenities', v)}
            testidPrefix="amenity"
          />
          <ChipGroup
            label="Things guests can do here or nearby"
            options={EXPERIENCE_OPTIONS}
            value={form.experiences}
            onToggle={v => toggleArr('experiences', v)}
            testidPrefix="experience"
          />
        </Section>

        <Section title="Tell your story">
          <Field label="One-line tagline" testid="tagline">
            <input value={form.tagline} onChange={e => update('tagline', e.target.value)} placeholder="e.g. Above the apple line" className={inputC} maxLength={200} />
          </Field>
          <Field label="Description (we'll polish this with you — leave blank if you prefer we write it)" testid="description">
            <textarea value={form.description} onChange={e => update('description', e.target.value)} rows={5} placeholder="What makes your home special? Who is it for?" className={inputC + ' resize-none'} maxLength={2000} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Best for (comma-separated)" testid="best_for">
              <input value={form.best_for} onChange={e => update('best_for', e.target.value)} placeholder="couples, families, solo" className={inputC} />
            </Field>
            <Field label="Experience types (comma-separated)" testid="experience_types">
              <input value={form.experience_types} onChange={e => update('experience_types', e.target.value)} placeholder="forest, river, orchards" className={inputC} />
            </Field>
          </div>
        </Section>

        <Section title="Photos (optional — but the most important part)">
          {/* Mobile-first upload tile */}
          <label className="block cursor-pointer" data-testid="apply-photo-upload-label">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
              data-testid="apply-photo-input"
            />
            <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:border-stone-500 transition bg-stone-50">
              <Camera size={32} className="mx-auto text-stone-400 mb-2" strokeWidth={1.4} />
              <p className="text-sm font-medium text-stone-800 mb-1">
                {uploading ? 'Uploading…' : 'Tap to add photos from your phone'}
              </p>
              <p className="text-[11px] text-stone-500">JPG / PNG / HEIC · up to 12MB each · max 25 photos</p>
              {uploadedPhotos.length > 0 && (
                <p className="text-[11px] text-emerald-700 font-medium mt-2">
                  {uploadedPhotos.length} photo{uploadedPhotos.length !== 1 ? 's' : ''} ready
                </p>
              )}
            </div>
          </label>

          {/* Thumbnail grid */}
          {uploadedPhotos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3" data-testid="apply-photo-thumbnails">
              {uploadedPhotos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded overflow-hidden border border-stone-200 group">
                  <img src={p.thumbnail} alt={p.filename} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 bg-black/70 hover:bg-rose-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    aria-label="Remove photo"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* WhatsApp send-photos shortcut */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-3">
            <p className="text-sm font-medium text-emerald-900 mb-2 flex items-center gap-2">
              <MessageCircle size={16} /> Prefer to send photos via WhatsApp?
            </p>
            <p className="text-[11px] text-emerald-800 leading-relaxed mb-3">
              No problem. Submit this form first (we'll save your details), then send your photos to us on WhatsApp. We'll attach them to your application.
            </p>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi GoHimalayan — sending photos for my application: ${form.display_name || '[property name]'}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700"
              data-testid="apply-whatsapp-photos"
            >
              <MessageCircle size={14} /> Send photos via WhatsApp
            </a>
          </div>

          {/* Advanced: paste URLs (for desktop power users) */}
          <details className="mt-2">
            <summary className="text-[11px] text-stone-500 cursor-pointer hover:text-stone-800">Or paste photo URLs (Drive, Photos, etc.)</summary>
            <textarea
              value={form.image_urls_text}
              onChange={e => update('image_urls_text', e.target.value)}
              rows={3}
              placeholder={'https://drive.google.com/...\nhttps://photos.app.goo.gl/...'}
              className={inputC + ' font-mono text-xs resize-none mt-2'}
              data-testid="apply-image-urls-textarea"
            />
          </details>

          <p className="text-[11px] text-stone-500 leading-relaxed">
            <b>Editorial bar:</b> no people, no signage/watermarks, no bathroom shots. Hi-res landscape photos preferred. Our AI will auto-flag images that don't meet the bar — you'll get a chance to swap them.
          </p>
        </Section>

        <Section title="Optional">
          <Field label="Existing booking page URL (Booking.com, MakeMyTrip, your website…)" testid="booking_source_url">
            <input value={form.booking_source_url} onChange={e => update('booking_source_url', e.target.value)} placeholder="https://..." className={inputC} />
          </Field>
        </Section>

        {/* Host clickwrap — the discount mechanic only works if hosts know about it upfront */}
        <Section title="The deal">
          <div className="bg-amber-50/60 border border-amber-200 rounded-lg px-4 py-3.5 text-sm leading-relaxed" data-testid="apply-discount-consent">
            <p className="text-stone-900 font-medium mb-1.5">🎁 GoHimalayan Guest Discount Program</p>
            <p className="text-xs text-stone-700 leading-relaxed">
              When a guest enquires through GoHimalayan, they can opt in for a <b>10% discount code</b> (e.g.{' '}
              <code className="font-mono text-[11px] bg-white px-1.5 py-0.5 rounded border border-stone-200">WELCOME10-AB12</code>).
              The code arrives in their first WhatsApp message to you.
            </p>
            <p className="text-xs text-stone-700 leading-relaxed mt-2">
              <b>Why this works for you:</b> Booking.com / MakeMyTrip take 15–20% commission. GoHimalayan takes <b>zero</b>. So even with a 10% guest discount, you net more per booking and the guest still saves vs. OTAs. Win-win, and it's why guests choose GoHimalayan.
            </p>
            <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.consent_discount}
                onChange={e => update('consent_discount', e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-stone-400 text-amber-600 focus:ring-amber-500 cursor-pointer"
                data-testid="apply-consent-discount"
              />
              <span className="text-xs text-stone-800 leading-snug">
                <b>I agree to honour valid GoHimalayan discount codes (10% off the agreed nightly rate)</b> on bookings that come through this platform.
              </span>
            </label>
          </div>
        </Section>

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            type="submit"
            disabled={submitting || !form.consent_discount}
            className="flex-1 px-6 py-3 bg-stone-900 text-white rounded font-medium hover:bg-stone-800 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="apply-submit-btn"
          >
            {submitting ? 'Submitting…' : 'Submit application'} <ArrowRight size={16} />
          </button>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi GoHimalayan — I'd like to apply with my property.")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded font-medium hover:bg-emerald-700 flex items-center justify-center gap-2"
            data-testid="apply-whatsapp-fallback"
          >
            <MessageCircle size={16} /> Or WhatsApp us instead
          </a>
        </div>
        <p className="text-[11px] text-stone-400 text-center pt-2">
          We respond to every application within 48 hours. No commission. No middlemen. Ever.
        </p>
      </form>
    </div>
  );
}

const inputC = "w-full px-3 py-2 border border-stone-300 rounded text-sm focus:outline-none focus:border-stone-700 bg-white";

const Section = ({ title, children }) => (
  <section className="bg-white rounded-lg border border-stone-200 p-6 space-y-4">
    <h2 className="text-[10px] tracking-[0.3em] uppercase text-stone-400 font-semibold">{title}</h2>
    {children}
  </section>
);

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={`apply-field-${testid}`}>
    <span className="block text-xs text-stone-700 mb-1.5">{label}</span>
    {children}
  </label>
);

const InvField = ({ label, value, onChange, testid }) => (
  <label className="block" data-testid={`apply-field-${testid}`}>
    <span className="block text-[10px] tracking-wide uppercase text-stone-500 mb-1">{label}</span>
    <input
      type="number"
      min="0"
      max="50"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder="0"
      className="w-full px-2.5 py-1.5 border border-stone-300 rounded text-sm focus:outline-none focus:border-stone-700 bg-white text-center"
    />
  </label>
);

// ChipGroup — multi-select tag picker. Used for amenities + experiences in /apply.
// Tap toggles. Selected = filled green chip; unselected = ghost outline.
const ChipGroup = ({ label, options, value, onToggle, testidPrefix }) => {
  const sel = new Set(value || []);
  return (
    <div className="space-y-2" data-testid={`apply-chipgroup-${testidPrefix}`}>
      <p className="text-xs text-stone-700">{label} <span className="text-stone-400">({sel.size} selected)</span></p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => {
          const on = sel.has(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              data-testid={`apply-chip-${testidPrefix}-${opt.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}
              className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                on
                  ? 'bg-emerald-600 text-white border-emerald-700'
                  : 'bg-white text-stone-700 border-stone-300 hover:border-stone-500 hover:bg-stone-50'
              }`}
            >
              {on ? '✓ ' : ''}{opt}
            </button>
          );
        })}
      </div>
    </div>
  );
};

